import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@tCentro/theme/design";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
    onClickHandler?: (index: number) => void
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    productImageGridItem?: InjectableCss;
    imgStyles?: InjectableCss;
    imgContainer?: InjectableCss;
    countStyles?: InjectableCss;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    countStyles: {
        css: css`
            font-family: Poppins;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;
            text-align: left;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    expirationDateGridItem: {
        width: 12,
        css: css`
            margin-top: 6px !important;
            padding: 0px;
        `,
    },
    imgContainer: {
        css: css`
            width: 64px;
            height: 64px;
            aspect-ratio: 1/1;
            background-color: white;
            box-shadow: 0px 2px 8px 0px rgba(38, 37, 37, 0.16);
        `,
    },
    imgStyles: {
        css: css`
            width: 64px !important;
            height: 64px !important;
            object-fit: contain;
        `,
    },
    productImageGridItem: {
        css: css`
            display: flex;
            gap: 8px;
            margin-top: 8px;
            align-items: center;
            align-self: self-start;
        `,
    },
    expirationDateHeadingAndText: {
        wrapper: {
            css: css`
                flex-direction: row;
                flex-basis: 100%;
                max-width: 100%;
                align-items: center;
            `,
        },
        heading: {
            css: css`
                text-transform: capitalize;
                color: ${getProp("theme.typography.p.color")};
            `,
        },
        text: {
            css: css`
                font-family: Poppins;
                font-size: 14px;
                font-weight: 400;
                text-align: left;
                color: ${getProp("theme.typography.p.color")};
                width: auto;
                margin-left: 4px;
            `,
        },
    },
    orderDateHeadingAndText: {
        wrapper: {
            css: css`
                flex-direction: row;
                align-items: center;
            `,
        },
        heading: {
            css: css`
                text-transform: capitalize;
                color: ${getProp("theme.typography.p.color")};
            `,
        },
        text: {
            css: css`
                font-family: Poppins;
                font-size: 14px;
                font-weight: 400;
                text-align: left;
                color: ${getProp("theme.typography.p.color")};
                width: auto;
                margin-left: 4px;
            `,
        },
    },
    statusHeadingAndText: {
        wrapper: {
            css: css`
                flex-direction: row;
                align-items: center;
            `,
        },
        heading: {
            css: css`
                text-transform: capitalize;
                color: ${getProp("theme.typography.p.color")};
            `,
        },
        text: {
            css: css`
                font-family: Poppins;
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
                color: ${getProp("theme.typography.p.color")};
                width: auto;
                margin-left: 4px;
            `,
        },
    },
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
            padding-top: 32px;
            display: flex;
            flex-direction: column;
            row-gap: 16px;
        `,
    },
    container: {
        gap: 5,
        css: css`
            border-bottom: 2px solid #e0dfe0;
            padding-bottom: 16px;
        `,
    },
    quoteDetailsLinkGridItem: {
        width: 12,
        css: css`
            a > span {
                font-family: Poppins;
                font-size: 16px;
                font-weight: 600;
                line-height: 24px;
                text-align: right;
            }
        `,
    },
    typeGridItem: { width: 6 },
    statusGridItem: {
        width: 12,
        css: css`
            padding: 0px;
            margin-top: 8px !important;
        `,
    },
    orderDateGridItem: {
        width: 12,
        css: css`
            padding: 0px;
            margin-top: 6px !important;
        `,
    },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
};

const QuoteSummaryCard: FC<Props> = ({ quote, session, quoteSettings, extendedStyles, onClickHandler }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "long",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "long",
                day: "numeric",
            },
        });
    }
    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;

    const top3ProductsImagesSplit = quote?.properties?.top3ProductsImages?.split("|");
    const top3ProductsUrisSplit = quote?.properties?.top3ProductsUris?.split("|");

    return (
        <GridItem {...styles.quoteSummaryCardGridItem} data-test-selector="recentQuotes">
            <GridContainer {...styles.container}>
                <GridItem {...styles.quoteDetailsLinkGridItem}>
                    <RfqQuoteDetailsPageTypeLink title={`Quote ${quote.quoteNumber}`} quoteId={quote.id} />
                </GridItem>
                {quoteSettings.jobQuoteEnabled && (
                    <GridItem {...styles.typeGridItem}>
                        <SmallHeadingAndText
                            heading={translate("Type")}
                            text={quote.typeDisplay}
                            extendedStyles={styles.typeHeadingAndText}
                        />
                    </GridItem>
                )}
                <GridItem {...styles.statusGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Status: ")}
                        text={quote.statusDisplay}
                        extendedStyles={styles.statusHeadingAndText}
                    />
                </GridItem>
                <GridItem {...styles.orderDateGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Requested: ")}
                        text={orderDateDisplay}
                        extendedStyles={styles.orderDateHeadingAndText}
                    />
                </GridItem>
                <GridItem {...styles.expirationDateGridItem}>
                    {expirationDateDisplay && (
                        <SmallHeadingAndText
                            heading={translate("Valid Through: ")}
                            text={expirationDateDisplay}
                            extendedStyles={styles.expirationDateHeadingAndText}
                        />
                    )}
                </GridItem>
                {top3ProductsImagesSplit && (
                    <StyledWrapper {...styles.productImageGridItem}>
                        {top3ProductsImagesSplit?.map((imageUrl, index) => {
                            const productUrl = top3ProductsUrisSplit?.[index];
                            return (
                                <StyledWrapper {...styles.imgContainer} key={index}>
                                    <Link
                                        href={productUrl}
                                        css={css`
                                            width: 100%;
                                            height: 100%;
                                            > span {
                                                width: 100%;
                                                height: 100%;
                                            }
                                        `}
                                        onClick={() => onClickHandler?.(index)}
                                    >
                                        <Img src={imageUrl} altText={`Product Image ${index + 1}`} {...styles.imgStyles} />
                                    </Link>
                                </StyledWrapper>
                            );
                        })}
                        {quote?.properties?.remainingProductCount && (
                            <Typography as="span" {...styles.countStyles}>
                                +{quote?.properties?.remainingProductCount}
                            </Typography>
                        )}
                    </StyledWrapper>
                )}
            </GridContainer>
        </GridItem>
    );
};

export default QuoteSummaryCard;
